/**
 * ConvergenceMetricsDisplay Component
 *
 * Visual representation of convergence metrics at each round.
 */

import { clsx } from 'clsx';
import { TrendingUp, TrendingDown, Minus, Check, AlertCircle } from 'lucide-react';
import type { ConvergenceMetrics } from '../../../lib/api';

interface ConvergenceMetricsDisplayProps {
  metrics: ConvergenceMetrics;
  previousMetrics?: ConvergenceMetrics;
  compact?: boolean;
}

function getProgressBarColor(value: number): string {
  if (value >= 80) return 'bg-green-500';
  if (value >= 60) return 'bg-blue-500';
  if (value >= 40) return 'bg-yellow-500';
  return 'bg-red-500';
}

function getTrendIcon(current: number, previous?: number) {
  if (previous === undefined) return null;
  const diff = current - previous;
  if (diff > 5) return <TrendingUp className="h-3 w-3 text-green-500" />;
  if (diff < -5) return <TrendingDown className="h-3 w-3 text-red-500" />;
  return <Minus className="h-3 w-3 text-gray-400" />;
}

export function ConvergenceMetricsDisplay({
  metrics,
  previousMetrics,
  compact = false,
}: ConvergenceMetricsDisplayProps) {
  if (compact) {
    return (
      <div className="flex items-center gap-4 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
        {/* Matching Criteria Overlap */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Overlap:</span>
          <div className="flex items-center gap-1">
            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={clsx('h-full rounded-full', getProgressBarColor(metrics.criteriaOverlap))}
                style={{ width: `${metrics.criteriaOverlap}%` }}
              />
            </div>
            <span className="text-xs font-medium text-gray-700">{metrics.criteriaOverlap}%</span>
            {getTrendIcon(metrics.criteriaOverlap, previousMetrics?.criteriaOverlap)}
          </div>
        </div>

        {/* Position Stability */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">Stability:</span>
          <div className="flex items-center gap-1">
            <div className="w-16 h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className={clsx('h-full rounded-full', getProgressBarColor(metrics.positionChanges))}
                style={{ width: `${metrics.positionChanges}%` }}
              />
            </div>
            <span className="text-xs font-medium text-gray-700">{metrics.positionChanges}%</span>
            {getTrendIcon(metrics.positionChanges, previousMetrics?.positionChanges)}
          </div>
        </div>

        {/* Status Badges */}
        <div className="flex items-center gap-2">
          {metrics.isConverged && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-green-100 text-green-700">
              <Check className="h-3 w-3" />
              Converged
            </span>
          )}
          {metrics.isStable && (
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-blue-100 text-blue-700">
              Stable
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-gray-700">Convergence Analysis</h4>
        <div className="flex items-center gap-2">
          {metrics.isConverged && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700 border border-green-200">
              <Check className="h-3 w-3" />
              Converged
            </span>
          )}
          {metrics.isStable && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700 border border-blue-200">
              Stable
            </span>
          )}
          {!metrics.isConverged && !metrics.isStable && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-700 border border-amber-200">
              <AlertCircle className="h-3 w-3" />
              In Progress
            </span>
          )}
        </div>
      </div>

      {/* Progress Bars */}
      <div className="space-y-3">
        {/* Matching Criteria Overlap */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-600">Criteria Overlap</span>
            <div className="flex items-center gap-1">
              <span className="text-sm font-medium text-gray-700">{metrics.criteriaOverlap}%</span>
              {getTrendIcon(metrics.criteriaOverlap, previousMetrics?.criteriaOverlap)}
            </div>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={clsx('h-full rounded-full transition-all', getProgressBarColor(metrics.criteriaOverlap))}
              style={{ width: `${metrics.criteriaOverlap}%` }}
            />
          </div>
        </div>

        {/* Position Stability */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs text-gray-600">Position Stability</span>
            <div className="flex items-center gap-1">
              <span className="text-sm font-medium text-gray-700">{metrics.positionChanges}%</span>
              {getTrendIcon(metrics.positionChanges, previousMetrics?.positionChanges)}
            </div>
          </div>
          <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={clsx('h-full rounded-full transition-all', getProgressBarColor(metrics.positionChanges))}
              style={{ width: `${metrics.positionChanges}%` }}
            />
          </div>
        </div>

        {/* Ranking Correlation (if available) */}
        {metrics.rankingCorrelation !== undefined && (
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs text-gray-600">Ranking Correlation</span>
              <span className="text-sm font-medium text-gray-700">
                {metrics.rankingCorrelation.toFixed(2)}
              </span>
            </div>
            <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={clsx(
                  'h-full rounded-full transition-all',
                  getProgressBarColor((metrics.rankingCorrelation + 1) * 50) // Convert -1..1 to 0..100
                )}
                style={{ width: `${((metrics.rankingCorrelation + 1) / 2) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Consensus & Divergence Points */}
      {(metrics.consensusPoints?.length > 0 || metrics.divergencePoints?.length > 0) && (
        <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-100">
          {/* Consensus Points */}
          {metrics.consensusPoints?.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-green-700 mb-1 flex items-center gap-1">
                <Check className="h-3 w-3" />
                Consensus ({metrics.consensusPoints.length})
              </h5>
              <ul className="space-y-0.5">
                {metrics.consensusPoints.slice(0, 3).map((point, i) => (
                  <li key={i} className="text-xs text-gray-600 truncate" title={point}>
                    • {point}
                  </li>
                ))}
                {metrics.consensusPoints.length > 3 && (
                  <li className="text-xs text-gray-400">
                    +{metrics.consensusPoints.length - 3} more
                  </li>
                )}
              </ul>
            </div>
          )}

          {/* Divergence Points */}
          {metrics.divergencePoints?.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-amber-700 mb-1 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Divergence ({metrics.divergencePoints.length})
              </h5>
              <ul className="space-y-0.5">
                {metrics.divergencePoints.slice(0, 3).map((point, i) => (
                  <li key={i} className="text-xs text-gray-600 truncate" title={point}>
                    • {point}
                  </li>
                ))}
                {metrics.divergencePoints.length > 3 && (
                  <li className="text-xs text-gray-400">
                    +{metrics.divergencePoints.length - 3} more
                  </li>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Mini convergence badge for inline display
 */
export function ConvergenceBadge({ metrics }: { metrics: ConvergenceMetrics }) {
  return (
    <span
      className={clsx(
        'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium',
        metrics.isConverged
          ? 'bg-green-100 text-green-700 border border-green-200'
          : metrics.criteriaOverlap >= 70
            ? 'bg-blue-100 text-blue-700 border border-blue-200'
            : 'bg-amber-100 text-amber-700 border border-amber-200'
      )}
    >
      {metrics.criteriaOverlap}% overlap
      {metrics.isConverged && <Check className="h-3 w-3" />}
    </span>
  );
}
